import React, { useState, useMemo } from "react";
import classNames from "classnames";
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    Spinner,
    CardHeader,
    CardTitle,
    Modal,
    ModalBody,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable";
import ReactTable2 from "components/ReactTable/ReactTable2";
import { useGetChannels } from "api/channels/service";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getChannelTypeOptions } from "../../../constants/channelTypes";
import ChannelsEmbedded from "../../settings/Channels/ChannelsEmbedded";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useDeleteChannel } from "api/channels/service";
import ChannelsFilters from "views/settings/Channels/components/ChannelsFilters";
import { getTheme2SearchFilterFieldCss } from "utils/theme2MultiSelect";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";

const SA_CHANNELS_TAB_STYLES = (isTheme2) => `
  ${isTheme2 ? `
  .tenant-edit-page.tenant-edit-page--theme2 .sa-channels-tab .sa-channels-table-card .card-body {
    padding: 15px;
    padding-top: 10px;
    padding-bottom: 0 !important;
  }
  ${getTheme2SearchFilterFieldCss({
        pageScopeSelector: ".tenant-edit-page.tenant-edit-page--theme2 .sa-channels-tab",
        filtersSelector: ".sa-channels-table-card .sa-channels-filters",
        rowClass: "search-channels-filters-row",
        colClass: "search-channels-filter-col",
    })}
  .tenant-edit-page.tenant-edit-page--theme2 .sa-channels-tab .search-channels-filters-row {
    column-gap: 4px;
  }
  .tenant-edit-page.tenant-edit-page--theme2 .sa-channels-tab .search-channels-filter-col {
    padding-left: 4px;
    padding-right: 4px;
  }
  .tenant-edit-page.tenant-edit-page--theme2 .sa-channels-tab .search-channels-filter-col:first-child {
    padding-left: 0;
  }
  .tenant-edit-page.tenant-edit-page--theme2 .sa-channels-tab .sa-channels-table-card .inventory-table-wrap {
    margin-top: 12px;
  }
  ` : ""}
  .tenant-edit-page .sa-channels-tab .sa-channels-card-header.card-header {
    padding-top: 2px;
    padding-bottom: 0;
  }
  .tenant-edit-page .sa-channels-tab .sa-channels-card-header h2,
  .tenant-edit-page .sa-channels-tab .sa-channels-card-header h4 {
    font-size: 16px !important;
    margin-bottom: 0;
    line-height: 1.2;
  }
  .sticky-stock-table .rt-table {
    width: 100%;
    min-width: 100%;
    border-collapse: collapse;
  }
  ${getStickyStockTableStickyColumnCss()}
`;

const SAChannelsTab = ({ activeColor = "blue", isTheme2 = false }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const [selectedDeleteId, setSelectedDeleteId] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const deleteChannelMutation = useDeleteChannel();

    const [isAddingChannel, setIsAddingChannel] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [searchType, setSearchType] = useState([]);
    const [debouncedSearch, setDebouncedSearch] = useState("");

    React.useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(handler);
    }, [search]);

    const typeOptions = useMemo(() => getChannelTypeOptions(t), [t]);

    const ChannelsTable = isTheme2 ? ReactTable2 : ReactTable;
    const { data: channelsData, isLoading } = useGetChannels(
        searchType.map(opt => opt.value),
        page,
        pageSize,
        debouncedSearch
    );

    const handleDeleteClick = (id) => {
        setSelectedDeleteId(id);
        setDeleteModalOpen(true);
    };
    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setSelectedDeleteId(null);
    };

    const handleConfirmDelete = () => {
        if (selectedDeleteId) {
            deleteChannelMutation.mutate(selectedDeleteId, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    setSelectedDeleteId(null);
                }
            });
        }
    };
    const columns = useMemo(
        () => [
            { Header: t('id'), accessor: "channel_id" },
            { Header: t('name'), accessor: "name" },
            { Header: t('description'), accessor: "description" },
            { Header: t('type'), accessor: "type" },
            { Header: t('status'), accessor: "status" },
            {
                Header: "",
                id: "actions",
                Cell: ({ row }) => {
                    const channel = row.original;
                    return (
                        <div className="actions-right" style={{ textAlign: 'right' }}>
                            <Button
                                color="link"
                                size="sm"
                                className="btn-icon btn-link like table-edit-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/channels/${channel.channel_id}`);
                                }}
                            >
                                <i className="tim-icons icon-pencil" />
                            </Button>
                            <Button
                                color="link"
                                size="sm"
                                className="btn-icon btn-link like table-delete-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(channel.documentId);
                                }}
                                disabled={deleteChannelMutation.isPending}
                            >
                                {deleteChannelMutation.isPending && selectedDeleteId === channel.documentId ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    <i className="tim-icons icon-simple-remove" />
                                )}
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [t, deleteChannelMutation.isPending, navigate, selectedDeleteId]
    );
    const rows = useMemo(() => {
        const list = channelsData?.results || [];
        return list.map((channel) => ({
            channel_id: channel.channel_id,
            documentId: channel.documentId,
            name: channel.legal_name,
            description: channel.name,
            type: channel.type ? t(`channels.types.${channel.type}`, { defaultValue: channel.type }) : "",
            status: channel.channel_status
                ? channel.channel_status.charAt(0).toUpperCase() + channel.channel_status.slice(1)
                : "",
        }));
    }, [channelsData, t]);

    const headerActions = (
        <Button
            size="sm"
            style={{ ...activeColorStyle, color: "white" }}
            onClick={() => navigate("/channels/add")}
        >
            {t("channels.addChannel")}
        </Button>
    );

    const titleHeader = (
        <CardHeader className="sa-channels-card-header">
            <div className="d-flex justify-content-between align-items-center">
                {isTheme2 ? (
                    <h2 className="mb-0">{t("channels.title")}</h2>
                ) : (
                    <CardTitle tag="h4" style={{ margin: 0 }}>{t("channels.title")}</CardTitle>
                )}
                {headerActions}
            </div>
        </CardHeader>
    );

    const filters = (
        <div className="sa-channels-filters">
            <ChannelsFilters
                activeColor={activeColor}
                isTheme2={isTheme2}
                search={search}
                onSearchChange={setSearch}
                searchType={searchType}
                onSearchTypeChange={setSearchType}
                typeOptions={typeOptions}
            />
        </div>
    );

    const table = (
        <div
            className={isTheme2 ? "inventory-table-wrap" : undefined}
            style={isTheme2 ? { fontSize: "13px", minWidth: 0, maxWidth: "100%" } : undefined}
        >
            {isTheme2 ? (
                <ChannelsTable
                    activeColor={activeColor}
                    data={rows}
                    columns={columns}
                    disableViewportClamp
                    className="-striped -highlight sticky-stock-table"
                    manualPagination={true}
                    pageCount={channelsData?.pagination?.pageCount || 1}
                    pageIndex={page - 1}
                    pageSize={pageSize}
                    totalItems={channelsData?.pagination?.total}
                    loading={isLoading}
                    loadingText={t("channels.loading")}
                    noDataText={t("channels.noChannelsFound")}
                    onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                    onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                    onRowClick={(row) => navigate(`/channels/${row.channel_id}`)}
                />
            ) : isLoading ? (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "200px" }}
                >
                    <div
                        className="spinner-border"
                        role="status"
                        style={{ color: activeColorStyle.borderColor }}
                    >
                        <span className="sr-only">{t("channels.loading")}</span>
                    </div>
                </div>
            ) : !channelsData || !channelsData.results || channelsData.results.length === 0 ? (
                <div>{t("channels.noChannelsFound")}</div>
            ) : (
                <ChannelsTable
                    activeColor={activeColor}
                    data={rows}
                    columns={columns}
                    className="-striped -highlight "
                    manualPagination={true}
                    pageCount={channelsData?.pagination?.pageCount || 1}
                    pageIndex={page - 1}
                    pageSize={pageSize}
                    onPageChange={(pageIndex) => setPage(pageIndex + 1)}
                    onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                    onRowClick={(row) => navigate(`/channels/${row.channel_id}`)}
                />
            )}
        </div>
    );

    const tableHeader =
        !isLoading && channelsData?.pagination?.total > 0 ? (
            <div style={{ fontWeight: 600, fontSize: ".8rem", color: "#333", letterSpacing: "0.02em", textTransform: "uppercase" }}>
                {channelsData?.pagination?.total} {t("searchDeals.recordsInTotal")}
            </div>
        ) : null;

    if (isAddingChannel) {
        return (
            <ChannelsEmbedded
                activeColor={activeColor}
                isTheme2={isTheme2}
                onClose={() => setIsAddingChannel(false)}
                noCard={true}
                onCreated={() => setIsAddingChannel(false)}
                isDisabled={false}
            />
        );
    }

    return (
        <div
            className={classNames("sa-channels-tab", {
                "sa-channels-tab--theme2": isTheme2,
            })}
        >
            <style>{SA_CHANNELS_TAB_STYLES(isTheme2)}</style>
            <Row>
                <Col md="12">
                    {isTheme2 ? (
                        <Card className="sa-channels-table-card shadow-none border-0 mb-0">
                            {titleHeader}
                            <CardBody>
                                {filters}
                                {table}
                            </CardBody>
                        </Card>
                    ) : (
                        <>
                            <Card className="sa-channels-filter-card">
                                {titleHeader}
                                <CardBody>{filters}</CardBody>
                            </Card>
                            <Card className="mt-2">
                                {tableHeader ? <CardHeader>{tableHeader}</CardHeader> : null}
                                <CardBody className="pt-0">{table}</CardBody>
                            </Card>
                        </>
                    )}
                </Col>
            </Row>
            <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
                <div className="modal-header justify-content-center">
                    <button
                        aria-hidden={true}
                        className="close"
                        data-dismiss="modal"
                        type="button"
                        onClick={handleCancelDelete}
                    >
                        <i className="tim-icons icon-simple-remove" />
                    </button>
                    <h6 className="title title-up">{t('channels.deleteChannel')}</h6>
                </div>
                <ModalBody className="text-center">
                    <p>{t('channels.deleteConfirmation')}</p>
                </ModalBody>
                <div className="modal-footer">
                    <Button
                        color="secondary"
                        type="button"
                        onClick={handleCancelDelete}
                        disabled={deleteChannelMutation.isPending}
                    >
                        {t('channels.cancel')}
                    </Button>
                    <Button
                        style={{
                            background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
                            backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
                            borderColor: '#fd5d93',
                            color: 'white'
                        }}
                        color="danger"
                        type="button"
                        onClick={handleConfirmDelete}
                        disabled={deleteChannelMutation.isPending}
                    >
                        {deleteChannelMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                {t('channels.deleting')}
                            </>
                        ) : (
                            t('channels.yesDelete')
                        )}
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default SAChannelsTab;
